import { IconCheck, IconLoader2, IconPencil } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import React, { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { NSInput } from 'bricks';
import RenderIf from 'components/RenderIf/RenderIf';
import useToast from 'hooks/useToast';
import { IUploadedProForma } from 'types/db/UploadedProForma';
import { useUpdateUploadedProFormaDisplayName } from '../../../../queries/UploadedProForma';
import {
    getOriginalProFormaFileName,
    getProFormaDisplayName,
    sanitizeProFormaDisplayNameInput,
} from '../../helpers';

interface EditableDisplayNameProps {
    uploadedProForma: IUploadedProForma;
    organizationId: string;
    dealId: number;
    className?: string;
    textClassName?: string;
}

const EditableDisplayName = ({
    uploadedProForma,
    organizationId,
    dealId,
    className = '',
    textClassName = 'text-dark',
}: EditableDisplayNameProps) => {
    const [isEditing, setIsEditing] = useState(false);
    const [draftName, setDraftName] = useState('');
    const [optimisticName, setOptimisticName] = useState<string | null>(null);

    const queryClient = useQueryClient();
    const { showSuccess, showError } = useToast();
    const {
        mutate: updateDisplayName,
        isPending: isSaving,
    } = useUpdateUploadedProFormaDisplayName();

    const focusRef = useCallback((node: HTMLInputElement | null) => { node?.focus(); }, []);

    const displayLabel = optimisticName ?? getProFormaDisplayName(uploadedProForma);

    const startEditing = () => {
        if (isSaving) return;
        setDraftName(displayLabel);
        setIsEditing(true);
    };

    const cancelEditing = () => {
        setIsEditing(false);
        setDraftName('');
    };

    const commitName = () => {
        if (isSaving) return;

        const sanitized = sanitizeProFormaDisplayNameInput(draftName);
        const nameToSend = sanitized === '' ? '' : sanitized;
        const optimisticLabel = nameToSend === ''
            ? getOriginalProFormaFileName(uploadedProForma)
            : nameToSend;

        setOptimisticName(optimisticLabel);
        setDraftName('');

        updateDisplayName({
            organizationUuid: organizationId,
            uploadedProFormaId: uploadedProForma.id,
            displayName: nameToSend,
        }, {
            onSuccess: (data) => {
                queryClient.setQueryData(
                    ['UploadedProForma', organizationId, dealId, uploadedProForma.id],
                    data,
                );
                queryClient.invalidateQueries({ queryKey: ['UploadedProForma', organizationId, dealId, uploadedProForma.id] });
                queryClient.invalidateQueries({ queryKey: ['getUploadedProFormas', dealId] });
                queryClient.invalidateQueries({ queryKey: ['deals-with-pro-formas'] });
                showSuccess('Pro forma name updated.');
            },
            onError: () => showError('Failed to update pro forma name.'),
            onSettled: () => {
                setOptimisticName(null);
                setIsEditing(false);
            },
        });
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            commitName();
        }
        if (e.key === 'Escape') {
            e.preventDefault();
            cancelEditing();
        }
    };

    return (
        <div className={`d-flex align-items-center gap-2 ${className}`}>
            <RenderIf isTrue={!isEditing}>
                <span className={textClassName}>{displayLabel}</span>
                <NSIcon
                    icon={IconPencil}
                    size="xs"
                    className={'cursor--pointer ml-2'}
                    role="button"
                    tabIndex={0}
                    aria-label="Edit pro forma name"
                    onClick={startEditing}
                    onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            startEditing();
                        }
                    }}
                />
            </RenderIf>
            <RenderIf isTrue={isEditing}>
                <NSInput
                    name="editableDisplayName"
                    type="text"
                    value={draftName}
                    innerRef={focusRef}
                    style={{ maxWidth: '28rem' }}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDraftName(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={cancelEditing}
                />
                <NSIcon
                    icon={isSaving ? IconLoader2 : IconCheck}
                    spin={isSaving}
                    size="xs"
                    className={'cursor--pointer ml-2'}
                    role="button"
                    tabIndex={0}
                    aria-label={isSaving ? 'Saving name' : 'Save name'}
                    onMouseDown={e => {
                        e.preventDefault();
                        commitName();
                    }}
                />
            </RenderIf>
        </div>
    );
};

export default EditableDisplayName;
